/**
 * One word list for the health of an integration and the outcome of a row,
 * the same on every screen (doc 07, "Words and tones"). Always shown as a word
 * and an icon, never colour alone.
 */

export type Tone = "neutral" | "info" | "good" | "warning" | "danger";

export type HealthKey =
  | "failing"
  | "paused_by_guard"
  | "needs_answer"
  | "needs_update"
  | "late"
  | "building"
  | "running"
  | "setting_up"
  | "paused"
  | "off"
  | "on_time";

export type HealthIcon =
  | "pencil"
  | "spinner"
  | "check"
  | "clock"
  | "question"
  | "pause"
  | "cross"
  | "refresh"
  | "power";

export type Health = {
  key: HealthKey;
  word: string;
  tone: Tone;
  icon: HealthIcon;
  /** One sentence saying why. */
  why: string;
  /** Lower is more trouble; lists sort by it. */
  rank: number;
};

const HEALTH: Record<HealthKey, Omit<Health, "why" | "key">> = {
  failing: { word: "Failing", tone: "danger", icon: "cross", rank: 0 },
  paused_by_guard: { word: "Paused by a guard", tone: "danger", icon: "pause", rank: 1 },
  needs_answer: { word: "Needs your answer", tone: "warning", icon: "question", rank: 2 },
  needs_update: { word: "Needs update", tone: "warning", icon: "refresh", rank: 3 },
  late: { word: "Late", tone: "warning", icon: "clock", rank: 4 },
  building: { word: "Building scraper", tone: "info", icon: "spinner", rank: 5 },
  running: { word: "Running", tone: "info", icon: "spinner", rank: 6 },
  setting_up: { word: "Setting up", tone: "neutral", icon: "pencil", rank: 7 },
  paused: { word: "Paused", tone: "neutral", icon: "pause", rank: 8 },
  off: { word: "Off", tone: "neutral", icon: "power", rank: 9 },
  on_time: { word: "On time", tone: "good", icon: "check", rank: 10 },
};

export const HEALTH_KEYS = Object.keys(HEALTH) as HealthKey[];

export function healthWord(key: HealthKey): string {
  return HEALTH[key].word;
}

function make(key: HealthKey, why: string): Health {
  return { key, why, ...HEALTH[key] };
}

export type HealthInput = {
  state: string;
  pauseReason?: string | null;
  openAnswers?: number;
  needsUpdate?: string[];
  working?: string | null;
  lastOutcome?: string | null;
  lastError?: string | null;
  lastRunAt?: string | null;
  lastCheckedAt?: string | null;
  checkSeconds: number;
};

/** Scout's words for a job whose scraper its agent is still writing. */
const BUILDING = new Set(["draft", "exploring", "building"]);

/** Work in the queue that means "a pull is in progress". A check every minute is not. */
const PULLING = new Set(["pull", "run", "replay"]);

function firstLine(text: string): string {
  const line = text.split("\n")[0].trim();
  return line.length > 240 ? `${line.slice(0, 239)}…` : line;
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

/**
 * How an integration is doing.
 *
 * A guard's pause comes before its question, because the pause is what a
 * person has to act on; the question is how they act.
 */
export function healthOf(
  item: HealthInput,
  options: { now?: number; scoutJobStatus?: string | null } = {},
): Health {
  const now = options.now ?? Date.now();
  if (item.state === "paused_by_guard") {
    return make("paused_by_guard", item.pauseReason || "A safety guard stopped it. Nothing more was written.");
  }
  if (item.state === "paused") return make("paused", item.pauseReason || "A person paused it. Scout runs are not read.");
  if (item.state === "off") return make("off", "It is switched off. Scout runs are not read.");
  if ((item.openAnswers ?? 0) > 0) {
    const count = item.openAnswers ?? 0;
    return make("needs_answer", `${plural(count, "thing")} ${count === 1 ? "is" : "are"} waiting for a person.`);
  }
  if ((item.needsUpdate ?? []).length > 0) {
    return make("needs_update", `The workflow's import changed. Map: ${(item.needsUpdate ?? []).join(", ")}.`);
  }
  if (item.state === "setting_up") {
    if (options.scoutJobStatus && BUILDING.has(options.scoutJobStatus.toLowerCase())) {
      return make("building", "Scout's agent is writing the scraper.");
    }
    return make("setting_up", "Not switched on yet.");
  }
  if (item.working && PULLING.has(item.working)) return make("running", "A pull is in progress.");
  if (item.lastOutcome === "failed") {
    return make("failing", item.lastError ? firstLine(item.lastError) : "The last run failed.");
  }
  const heard = [item.lastRunAt, item.lastCheckedAt]
    .filter((moment): moment is string => Boolean(moment))
    .map((moment) => new Date(moment).getTime())
    .filter((moment) => Number.isFinite(moment));
  if (heard.length === 0) return make("on_time", "Waiting for its first run.");
  const newest = Math.max(...heard);
  const age = (now - newest) / 1000;
  // The scheduler checks when nothing came within the check time, so a little
  // past it is normal. Half as long again, plus two minutes, is not.
  if (age > item.checkSeconds * 1.5 + 120) {
    return make("late", `Nothing came in for ${agoWords(age)}. It checks every ${everyWords(item.checkSeconds)}.`);
  }
  return make("on_time", item.lastRunAt ? `Last run ${ago(item.lastRunAt, now)}.` : "Checked recently.");
}

export type HealthBucket = "on_time" | "needs_look" | "setting_up" | "off";

/** The four tiles on the list page. Running is on time; anything a person must act on needs a look. */
export function healthBucket(key: HealthKey): HealthBucket {
  switch (key) {
    case "on_time":
    case "running":
      return "on_time";
    case "setting_up":
    case "building":
      return "setting_up";
    case "paused":
    case "off":
      return "off";
    default:
      return "needs_look";
  }
}

/** Trouble first, then by name. */
export function troubleFirst<T extends { name: string }>(rows: T[], health: (row: T) => Health): T[] {
  return [...rows].sort((a, b) => health(a).rank - health(b).rank || a.name.localeCompare(b.name));
}

// --------------------------------------------------------------------------- rows

export type OutcomeKey =
  | "accepted"
  | "unchanged"
  | "kept_by_hand"
  | "skipped"
  | "not_placed"
  | "held"
  | "refused"
  | "problem"
  | "ready";

export const OUTCOMES: { key: OutcomeKey; word: string; tone: Tone; means: string }[] = [
  { key: "accepted", word: "Accepted", tone: "good", means: "Written." },
  { key: "ready", word: "Ready", tone: "good", means: "Would be sent." },
  { key: "unchanged", word: "Unchanged", tone: "neutral", means: "Same as last time, nothing sent." },
  { key: "kept_by_hand", word: "Kept by hand", tone: "info", means: "A person set this field; left alone." },
  { key: "skipped", word: "Skipped", tone: "neutral", means: "A keep rule left it out." },
  { key: "not_placed", word: "Not placed", tone: "warning", means: "No record fits and the setting is skip." },
  { key: "held", word: "Held", tone: "warning", means: "Waiting for a person." },
  { key: "problem", word: "Could not read", tone: "danger", means: "A value could not be read, or a needed field is empty." },
  { key: "refused", word: "Refused", tone: "danger", means: "A workflow rule said no." },
];

export function outcomeOf(key: string): { key: string; word: string; tone: Tone; means: string } {
  return (
    OUTCOMES.find((one) => one.key === key) ?? {
      key,
      word: key.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()),
      tone: "neutral",
      means: "",
    }
  );
}

/** Counts that are outcomes of rows, in the word list's order. Other counts (created, updated) are left out. */
export function outcomeCounts(counts: Record<string, number>): { key: string; word: string; tone: Tone; count: number }[] {
  return OUTCOMES.filter((one) => (counts[one.key] ?? 0) > 0).map((one) => ({
    key: one.key,
    word: one.word,
    tone: one.tone,
    count: counts[one.key] ?? 0,
  }));
}

/** Counts that are not outcomes: records created, updated. */
export function extraCounts(counts: Record<string, number>): string[] {
  const known = new Set<string>(OUTCOMES.map((one) => one.key));
  return Object.entries(counts)
    .filter(([key, count]) => !known.has(key) && count > 0)
    .map(([key, count]) => `${key.replace(/_/g, " ")} ${count.toLocaleString()}`);
}

// --------------------------------------------------------------------------- runs

const RUN_STATUS: Record<string, { word: string; tone: Tone }> = {
  written: { word: "Written", tone: "good" },
  nothing_new: { word: "Nothing new", tone: "neutral" },
  older: { word: "Older than what we have", tone: "neutral" },
  paused_by_guard: { word: "Paused by a guard", tone: "danger" },
  failed: { word: "Failed", tone: "danger" },
  tried: { word: "Tried, nothing written", tone: "info" },
  queued: { word: "Waiting", tone: "neutral" },
  running: { word: "Running", tone: "info" },
  cancelled: { word: "Stopped", tone: "neutral" },
};

export function runStatus(status: string): { word: string; tone: Tone } {
  return RUN_STATUS[status] ?? { word: status.replace(/_/g, " "), tone: "neutral" };
}

const SCOUT_STATUS: Record<string, { word: string; tone: Tone }> = {
  passed: { word: "Passed", tone: "good" },
  failed: { word: "Failed", tone: "danger" },
  rejected: { word: "Rejected by its check", tone: "danger" },
  blocked: { word: "Blocked by the site", tone: "danger" },
  running: { word: "Running", tone: "info" },
  checking: { word: "Being checked", tone: "info" },
  waiting: { word: "Waiting", tone: "neutral" },
  queued: { word: "Waiting", tone: "neutral" },
};

export function scoutRunStatus(status: string): { word: string; tone: Tone } {
  return SCOUT_STATUS[status.toLowerCase()] ?? { word: status, tone: "neutral" };
}

const SCOUT_JOB: Record<string, { word: string; tone: Tone }> = {
  active: { word: "Ready", tone: "good" },
  draft: { word: "Being built", tone: "info" },
  exploring: { word: "Being built", tone: "info" },
  needs_attention: { word: "Needs attention in Scout", tone: "warning" },
  paused: { word: "Paused in Scout", tone: "neutral" },
};

export function scoutJobStatus(status: string): { word: string; tone: Tone } {
  return SCOUT_JOB[status.toLowerCase()] ?? { word: status.replace(/_/g, " "), tone: "neutral" };
}

const VIA: Record<string, string> = {
  doorbell: "Scout rang the doorbell",
  check: "Found by omnium's check",
  run_now: "Run now",
  replay: "Replay",
  try: "Try",
};

export function viaWords(via: string): string {
  return VIA[via] ?? via;
}

const JOB_KIND: Record<string, string> = {
  run: "Asking Scout to run",
  pull: "Reading a run",
  check: "Checking for a new run",
  replay: "Replaying a run",
  try: "Trying a version",
};

export function jobKindWords(kind: string): string {
  return JOB_KIND[kind] ?? kind;
}

const ANSWER_KIND: Record<string, string> = {
  held_row: "A row is waiting",
  guard_pause: "Paused by a guard",
  new_values: "New values",
  new_column: "A new column",
  missing_column: "A column is missing",
  empty_column: "An empty column",
};

export function answerKindWords(kind: string): string {
  return ANSWER_KIND[kind] ?? kind.replace(/_/g, " ");
}

// --------------------------------------------------------------------------- time

export function agoWords(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return s <= 1 ? "a moment" : `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} days`;
}

export function ago(moment: string | null | undefined, now = Date.now()): string {
  if (!moment) return "never";
  const at = new Date(moment).getTime();
  if (!Number.isFinite(at)) return "never";
  const seconds = (now - at) / 1000;
  if (seconds < 0) return `in ${agoWords(-seconds)}`;
  if (seconds < 5) return "just now";
  return `${agoWords(seconds)} ago`;
}

export function everyWords(seconds: number): string {
  if (seconds % 86400 === 0) return seconds === 86400 ? "day" : `${seconds / 86400} days`;
  if (seconds % 3600 === 0) return seconds === 3600 ? "hour" : `${seconds / 3600} hours`;
  if (seconds % 60 === 0) return seconds === 60 ? "minute" : `${seconds / 60} min`;
  return `${seconds} s`;
}

/** A length of time on its own: "1 min", "6 hours", "1 day". */
export function durationWords(seconds: number): string {
  if (seconds > 0 && seconds % 86400 === 0) return `${seconds / 86400} ${seconds === 86400 ? "day" : "days"}`;
  if (seconds > 0 && seconds % 3600 === 0) return `${seconds / 3600} ${seconds === 3600 ? "hour" : "hours"}`;
  if (seconds > 0 && seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

export function when(moment: string | null | undefined, timeZone?: string): string {
  if (!moment) return "—";
  const date = new Date(moment);
  if (!Number.isFinite(date.getTime())) return "—";
  try {
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });
  } catch {
    return date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  }
}

/** "Games operations · import units" from `games` and `games.import_units`. */
export function sendsTo(workflowName: string, command: string): string {
  const tail = command.includes(".") ? command.slice(command.indexOf(".") + 1) : command;
  return `${workflowName} · ${tail.replace(/_/g, " ")}`;
}

/** What Scout said, first line only, with the HTML of an error page removed. */
export function plainError(text: string | null | undefined): string {
  if (!text) return "";
  const noHtml = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return noHtml.length > 300 ? `${noHtml.slice(0, 299)}…` : noHtml;
}
